import { Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

import { ConsentCategory, consentCovers, expireCookiesByPrefix, reportConsentError } from '../consent/consent';
import { PRODUCTION_HOST } from '../consent/production-host';
import { firstPathSegment, META_PIXEL_EXCLUDED_ROUTES, PixelStore, storeQualifiesForPixel } from './meta-pixel.service';

export const GOOGLE_ADS_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js';

/** The longest settled() lets the receipt wait for Google's script; see MetaPixelService. */
export const GOOGLE_ADS_MAX_WAIT_MS = 1000;

/**
 * Cookie name prefixes Google Ads writes on the registrable domain (`_gcl_aw`
 * holds the ad click, `_gcl_au` the first-party id). Expired on withdrawal.
 */
export const GOOGLE_ADS_COOKIE_PREFIXES = ['_gcl_'] as const;

/** A conversion's send_to: `AW-<account>/<label>`. */
const SEND_TO_RE = /^(AW-\d+)\/[\w-]+$/;

export interface GoogleAdsSettings {
  /** `AW-<account>`, the id gtag.js is loaded and configured with. */
  conversionId: string;
  /** The purchase conversion, `AW-<account>/<label>`. */
  sendTo: string;
}

/**
 * The go-live switch, read from `environment.googleAds`: null, and so off,
 * until a valid conversion is set. Loading also needs a production build on
 * the production host.
 */
export function googleAdsSettings(): GoogleAdsSettings | null {
  const { sendTo } = environment.googleAds;
  const match = sendTo ? SEND_TO_RE.exec(sendTo) : null;
  if (!sendTo || !match) return null;
  return { conversionId: match[1], sendTo };
}

/**
 * The Google Ads purchase conversion, honouring the `markedsforing` consent
 * category by loading or not loading. Everything that can send to Google Ads
 * goes through here.
 *
 * Built in code beside the Meta pixel rather than in Google Tag Manager, so
 * the two share one consent gate and one store rule: storeQualifiesForPixel,
 * imported, never copied. GTM loads on the statistics choice and holds Google
 * Analytics only.
 *
 * Basic consent mode. Nothing from Google loads before all of: a production
 * build, the switch set (googleAdsSettings), the exact production hostname, a
 * marketing yes the hosted consent banner says covers Google Ads (one given
 * since the banner named it), a buyer route, and a store that qualifies. Then gtag.js is loaded and the two ad
 * signals Google needs to count a conversion, `ad_storage` and `ad_user_data`,
 * are granted. `ad_personalization` stays denied, so nothing is used for
 * remarketing.
 *
 * The signals live in the one dataLayer the page shares with GTM, so Google
 * Analytics sees them too. They are granted when a store that qualifies is
 * declared and denied the moment a navigation starts, so every page after it
 * (a partner's store, the front page, a checkout step) sends its first hit
 * denied until it declares a store that qualifies. A partner's purchase never
 * carries them to Analytics' link with Google Ads either. A conversion still
 * queued when the receipt moves on is not caught: gtag.js reads the dataLayer
 * in order, so it reads the conversion under the grant before the deny.
 *
 * What Google Ads is told: the purchase conversion, with what iGive earns on
 * the order as the value, NOK, and the payment id as transaction id, which
 * Google uses to drop a second copy. Google's tag also sends one page-view
 * ping of its own from each document it loads in (address, page title, the
 * click id if the address has one); `send_page_view: false` stops the extra
 * page view and source update it would otherwise add. Loading on the store
 * page is what lets Google keep the ad click from the landing URL in its
 * `_gcl_aw` cookie until the buyer is back from the payment provider.
 */
@Injectable({ providedIn: 'root' })
export class GoogleAdsService implements OnDestroy {
  private loaded = false;
  /** Whether this document has told Google the ad signals are granted. */
  private granted = false;
  private scriptSettled: Promise<void> | null = null;
  private scriptDone = false;
  private storeQualifies = false;
  /** The navigation in flight, and whether the store on screen qualified when it started. */
  private navigationInFlight: { id: number; storeQualifies: boolean } | null = null;
  private navigation: Subscription;

  constructor(private router: Router) {
    // The store is forgotten when a navigation starts and given back when that
    // same navigation never lands. Matched by id: a skipped navigation never
    // started, so it has nothing to give back. A navigation that replaces one
    // still in flight keeps the first one's store: that page is still on screen.
    this.navigation = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const storeQualifies = this.navigationInFlight?.storeQualifies ?? this.storeQualifies;
        this.navigationInFlight = { id: event.id, storeQualifies };
        this.storeQualifies = false;
        this.updateSignals();
      } else if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        if (this.navigationInFlight?.id !== event.id) return;
        this.storeQualifies = this.navigationInFlight.storeQualifies;
        this.navigationInFlight = null;
        this.updateSignals();
      } else if (event instanceof NavigationEnd && this.navigationInFlight?.id === event.id) {
        this.navigationInFlight = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.navigation.unsubscribe();
  }

  /** Called through the consent gate on load and after every decision. Asks the banner, not the caller. */
  applyConsent(): void {
    this.activateIfAllowed(this.router.url);
  }

  /** Called through AnalyticsService once a page knows its store; null when it failed to. */
  setStore(store: PixelStore | null): void {
    this.storeQualifies = storeQualifiesForPixel(store);
    this.updateSignals();
  }

  /** Sends the purchase conversion, or nothing. */
  trackPurchase(transactionId: string, value: number): void {
    const settings = googleAdsSettings();
    if (!settings || !this.activateIfAllowed(this.router.url)) return;
    window.gtag?.('event', 'conversion', {
      send_to: settings.sendTo,
      value,
      currency: 'NOK',
      transaction_id: transactionId,
    });
  }

  /** Resolves once Google's script has run or failed, never later than `maxWaitMs`. */
  settled(maxWaitMs = GOOGLE_ADS_MAX_WAIT_MS): Promise<void> {
    if (!this.scriptSettled || this.scriptDone) return Promise.resolve();
    return Promise.race([
      this.scriptSettled,
      new Promise<void>((resolve) => setTimeout(resolve, maxWaitMs)),
    ]);
  }

  /**
   * Withdrawal, the same contract as MetaPixelService.revoke(): `scope` is the
   * NEW scope, a no-op while `markedsforing` survives. Otherwise the signals
   * are denied, Google's cookies expired, and the return value says whether
   * gtag.js is still executing in this document, which only a reload stops.
   *
   * Switched off, the _gcl_ cookies go with `statistikk` instead, exactly as
   * they did when the gate expired them beside GA4's, so production behaves
   * as before until the switch is set. Switched on, they belong to the
   * marketing choice that lets them be written, and a statistics withdrawal
   * keeps the stored ad click.
   */
  revoke(scope: readonly ConsentCategory[]): boolean {
    const owner: ConsentCategory = googleAdsSettings() ? 'markedsforing' : 'statistikk';
    if (scope.includes(owner)) return false;
    this.deny();
    try {
      expireCookiesByPrefix(GOOGLE_ADS_COOKIE_PREFIXES);
    } catch (e) {
      reportConsentError(e, 'googleAds.revoke.clearCookies');
    }
    return this.loaded;
  }

  /** Grants the signals where everything allows it and denies them everywhere else. */
  private updateSignals(): void {
    if (!this.activateIfAllowed(this.router.url)) this.deny();
  }

  /** Grants the signals and loads the tag when everything allows it. Returns whether it did. */
  private activateIfAllowed(url: string): boolean {
    const settings = googleAdsSettings();
    if (!settings || !this.mayRun(url)) return false;
    this.grant();
    this.ensureLoaded(settings);
    return true;
  }

  private mayRun(url: string): boolean {
    if (!environment.production || typeof window === 'undefined') return false;
    if (window.location.hostname !== PRODUCTION_HOST || typeof window.gtag !== 'function') return false;
    if (!this.storeQualifies) return false;
    if ((META_PIXEL_EXCLUDED_ROUTES as readonly string[]).includes(firstPathSegment(url))) return false;
    return consentCovers('googleAds');
  }

  private grant(): void {
    if (this.granted) return;
    try {
      window.gtag?.('consent', 'update', { ad_storage: 'granted', ad_user_data: 'granted' });
      this.granted = true;
    } catch (e) {
      reportConsentError(e, 'googleAds.grant');
    }
  }

  private deny(): void {
    if (!this.granted) return;
    // Cleared first: a throw must not leave the flag claiming a grant is live
    // when the next grant() would then skip re-sending it.
    this.granted = false;
    try {
      window.gtag?.('consent', 'update', { ad_storage: 'denied', ad_user_data: 'denied' });
    } catch (e) {
      reportConsentError(e, 'googleAds.deny');
    }
  }

  /**
   * gtag.js on the dataLayer index.html set up, after the grant above, so the
   * script reads the commands in order: denied defaults, the grant, then the
   * config. Idempotent, the flag set before the insert like the pixel's.
   */
  private ensureLoaded(settings: GoogleAdsSettings): void {
    if (this.loaded) return;
    this.loaded = true;
    try {
      window.gtag?.('config', settings.conversionId, { send_page_view: false });
      const script = document.createElement('script');
      script.async = true;
      script.src = `${GOOGLE_ADS_SCRIPT_URL}?id=${settings.conversionId}`;
      const settled = new Promise<void>((resolve) => {
        const done = () => {
          this.scriptDone = true;
          resolve();
        };
        script.addEventListener('load', done);
        script.addEventListener('error', done);
      });
      document.head.appendChild(script);
      this.scriptSettled = settled;
    } catch (e) {
      reportConsentError(e, 'googleAds.load');
    }
  }
}
